/**
 * PasswordChangeSection Component
 *
 * Allows authenticated users to change their password
 * Uses authService with real API integration
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { authService } from '@/features/auth/services/authService'
import { toast } from '@/lib/toast'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'

// Password change schema with validation
const changePasswordSchema = z
  .object({
    old_password: z.string().min(1, 'Current password is required'),
    new_password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

type ChangePasswordForm = z.infer<typeof changePasswordSchema>

export function PasswordChangeSection() {
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: (data: { old_password: string; new_password: string }) =>
      authService.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully')
      reset()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to change password')
    },
  })

  const onSubmit = (data: ChangePasswordForm) => {
    changePasswordMutation.mutate({
      old_password: data.old_password,
      new_password: data.new_password,
    })
  }

  return (
    <Card className='bg-transparent'>
      <CardHeader>
        <CardTitle>Change Password</CardTitle>
        <CardDescription>
          Update your password to keep your account secure
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          {/* Current Password */}
          <div className='space-y-2'>
            <Label htmlFor='old_password'>Current Password</Label>
            <div className='relative'>
              <Input
                id='old_password'
                type={showOldPassword ? 'text' : 'password'}
                {...register('old_password')}
                placeholder='Enter your current password'
                className={errors.old_password ? 'border-red-500' : ''}
              />
              <button
                type='button'
                onClick={() => setShowOldPassword(!showOldPassword)}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
              >
                {showOldPassword ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </button>
            </div>
            {errors.old_password && (
              <p className='text-sm text-red-500'>
                {errors.old_password.message}
              </p>
            )}
          </div>

          {/* New Password */}
          <div className='space-y-2'>
            <Label htmlFor='new_password'>New Password</Label>
            <div className='relative'>
              <Input
                id='new_password'
                type={showNewPassword ? 'text' : 'password'}
                {...register('new_password')}
                placeholder='Enter your new password'
                className={errors.new_password ? 'border-red-500' : ''}
              />
              <button
                type='button'
                onClick={() => setShowNewPassword(!showNewPassword)}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
              >
                {showNewPassword ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </button>
            </div>
            {errors.new_password && (
              <p className='text-sm text-red-500'>
                {errors.new_password.message}
              </p>
            )}
            <p className='text-xs text-muted-foreground'>
              Must be at least 8 characters with uppercase, lowercase, and a
              number
            </p>
          </div>

          {/* Confirm Password */}
          <div className='space-y-2'>
            <Label htmlFor='confirm_password'>Confirm New Password</Label>
            <div className='relative'>
              <Input
                id='confirm_password'
                type={showConfirmPassword ? 'text' : 'password'}
                {...register('confirm_password')}
                placeholder='Confirm your new password'
                className={errors.confirm_password ? 'border-red-500' : ''}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
              >
                {showConfirmPassword ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <p className='text-sm text-red-500'>
                {errors.confirm_password.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className='flex justify-end'>
            <Button
              type='submit'
              disabled={!isDirty || changePasswordMutation.isPending}
            >
              {changePasswordMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Changing Password...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
